import { Link } from 'react-router-dom';

interface ServingCommunitySectionProps {
  productName: string;
  productSlug: string;
}

const CITIES = [
  { name: 'Indianapolis', slug: 'indianapolis' },
  { name: 'Carmel', slug: 'carmel' },
  { name: 'Fishers', slug: 'fishers' },
  { name: 'Westfield', slug: 'westfield' },
  { name: 'Noblesville', slug: 'noblesville' },
  { name: 'Greenwood', slug: 'greenwood' },
  { name: 'Avon', slug: 'avon' },
  { name: 'Plainfield', slug: 'plainfield' },
  { name: 'Zionsville', slug: 'zionsville' },
  { name: 'Brownsburg', slug: 'brownsburg' },
  { name: 'Whitestown', slug: 'whitestown' },
  { name: 'Lebanon', slug: 'lebanon' },
  { name: 'Greenfield', slug: 'greenfield' },
  { name: 'Franklin', slug: 'franklin' },
  { name: 'Martinsville', slug: 'martinsville' },
  { name: 'Mooresville', slug: 'mooresville' },
  { name: 'Danville', slug: 'danville' },
  { name: 'Beech Grove', slug: 'beech-grove' },
  { name: 'Speedway', slug: 'speedway' },
  { name: 'Lawrence', slug: 'lawrence' },
  { name: 'Cumberland', slug: 'cumberland' },
  { name: 'McCordsville', slug: 'mccordsville' },
  { name: 'Fortville', slug: 'fortville' },
  { name: 'Bargersville', slug: 'bargersville' },
];

export default function ServingCommunitySection({ productName, productSlug }: ServingCommunitySectionProps) {
  return (
    <section className="py-16 bg-gradient-to-br from-orange-50 to-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <i className="ri-map-pin-line text-base"></i>
            Local Service Areas
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Find a Local {productName} Agent Near You
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We're proud to serve families and businesses throughout Central Indiana with personalized {productName.toLowerCase()} solutions
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
          {CITIES.map((city) => (
            <Link
              key={city.slug}
              to={`/service-areas/${city.slug}`}
              className="group bg-white rounded-xl p-5 border border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors flex-shrink-0">
                  <i className="ri-map-pin-2-fill text-lg text-orange-600 group-hover:text-white"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900 group-hover:text-orange-600 transition-colors truncate">
                    {city.name}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">
                    {productName} Agent
                  </p>
                </div>
                <i className="ri-arrow-right-line text-gray-400 group-hover:text-orange-600 transition-colors flex-shrink-0"></i>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/service-areas"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-all shadow-md hover:shadow-lg cursor-pointer"
          >
            View All Service Areas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-customer-service-2-line text-2xl text-orange-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Why Choose a Local Indianapolis {productName} Agent?
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Working with a local agent means personalized service, face-to-face consultations, and someone who understands the unique needs of Indiana families and businesses. We're here to help you find the right {productName.toLowerCase()} coverage for your specific situation.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-600 text-lg"></i>
                  <span className="text-sm text-gray-700 font-medium">Local expertise</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-600 text-lg"></i>
                  <span className="text-sm text-gray-700 font-medium">Personalized service</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-600 text-lg"></i>
                  <span className="text-sm text-gray-700 font-medium">Community focused</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flashing Limited Spots Urgency Strip */}
        <div className="mt-8 flex justify-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-amber-50 border-2 border-amber-400 rounded-2xl">
            <div className="w-2.5 h-2.5 flex items-center justify-center flex-shrink-0 relative">
              <span className="block w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping absolute"></span>
              <span className="block w-2.5 h-2.5 rounded-full bg-amber-500 relative"></span>
            </div>
            <i className="ri-flashlight-fill text-amber-600 text-base"></i>
            <span className="text-sm font-bold text-amber-800 uppercase tracking-wide animate-pulse">
              Limited Consultation Spots Available This Week — Book Before They Fill Up
            </span>
            <i className="ri-flashlight-fill text-amber-600 text-base"></i>
            <div className="w-2.5 h-2.5 flex items-center justify-center flex-shrink-0 relative">
              <span className="block w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping absolute"></span>
              <span className="block w-2.5 h-2.5 rounded-full bg-amber-500 relative"></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}